import {
  useState,
  useEffect,
  useCallback,
  ReactNode,
  MutableRefObject,
} from 'react';
import { Stack, GridLegacy, Divider } from '@mui/material';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridInitialState,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { IconCirclePlus, IconDownload, IconUpload } from '@tabler/icons-react';
import _ from 'lodash';
import { actionButtonSpacing, gridSpacing } from 'constants/themeConstants';
import { useDeviceContext } from 'hooks/useDeviceContext';
import {
  RowAction,
  ExtendedGridColDef,
  DatagridNames,
  DatagridRecordButtonProps,
  VisibleColumnGroup,
} from 'types/datagrid';
import { FullOrIconButton } from 'ui-component/DataGrid/components/FullOrIconButton';
import ExtendedQuickFilter from 'ui-component/DataGrid/ExtendedQuickFilter';
import ActionBar from 'ui-component/DataGrid/ExtendedGridToolbar/ActionBar';
import CustomExportButton from 'ui-component/DataGrid/ExtendedGridToolbar/CustomExportButton';
import { DatagridConfigurationControls } from 'ui-component/DataGrid/ExtendedGridToolbar/DatagridConfigurationControls';

type ExtendedGridToolbarProps<T, ReportDataType = never> = {
  apiRef: MutableRefObject<GridApiPremium>;
  columns: ExtendedGridColDef[];
  exportCsv?: () => void;
  fullCard?: boolean;
  handleResetState: () => void;
  handleSetColumnGroup: (name: string) => void;
  hideToolbar?: boolean;
  isExportLoading?: boolean;
  memoizedVisibleGroups?: VisibleColumnGroup[] | null;
  toolbarColumns?: boolean;
  toolbarExport?: boolean;
  toolbarFilters?: boolean;
  shouldSaveDatagridState: boolean;
  children?: ReactNode;
  exportFields: () => string[];
  exportFileNamePrefix?: string | null;
  gridName: DatagridNames;
  generateReportData?: ReportDataType;
  newRecordButton: DatagridRecordButtonProps;
  uploadRecordButton: DatagridRecordButtonProps;
  rowActions?: RowAction<T>[];
  selectedRowIds: string[];
  selectedRowData: T[];
  toolbarQuickFilter?: boolean;
  toolbarReset?: boolean;
  initialGridState: GridInitialState;
  enableStateQueryParams?: boolean;
  updateQueryParams?: (updates: Record<string, string | null>) => void;
};

const narrowWindowToolbarButtonSx = {
  width: '34px',
  height: '34px',
  borderRadius: '17px',
  minWidth: 'unset',
  '& .MuiButton-startIcon': { mx: 0 },
};

export default function ExtendedGridToolbar<T, ReportDataType>({
  apiRef,
  columns,
  exportCsv,
  fullCard,
  handleResetState,
  handleSetColumnGroup,
  isExportLoading,
  hideToolbar = false,
  memoizedVisibleGroups,
  toolbarColumns,
  toolbarExport,
  toolbarFilters,
  shouldSaveDatagridState,
  children,
  exportFields,
  exportFileNamePrefix,
  gridName,
  generateReportData,
  newRecordButton,
  uploadRecordButton,
  rowActions,
  selectedRowIds,
  selectedRowData,
  toolbarQuickFilter,
  toolbarReset,
  initialGridState,
  enableStateQueryParams,
  updateQueryParams,
}: ExtendedGridToolbarProps<T, ReportDataType>) {
  const { narrowWindow } = useDeviceContext();

  // Initialize search text from query params or initial state
  const initialSearchText =
    enableStateQueryParams &&
    initialGridState?.filter?.filterModel?.quickFilterValues?.[0]
      ? initialGridState.filter.filterModel.quickFilterValues[0]
      : '';

  const [searchText, setSearchText] = useState(initialSearchText);
  const [quickSearchFocused, setQuickSearchFocused] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const debouncedFilterUpdate = useCallback(
    _.debounce((value: string) => {
      if (apiRef?.current) {
        apiRef?.current.setQuickFilterValues([value]);
      }
      if (enableStateQueryParams && updateQueryParams) {
        updateQueryParams({
          quickFilter: value || null,
        });
      }
    }, 800),
    [enableStateQueryParams, updateQueryParams]
  );

  useEffect(() => {
    debouncedFilterUpdate(searchText);
  }, [searchText, debouncedFilterUpdate]);

  if (hideToolbar) return null;

  return (
    <GridToolbarContainer>
      <GridLegacy
        container
        sx={{
          px: fullCard ? actionButtonSpacing : undefined,
          pt: fullCard ? gridSpacing : undefined,
        }}
      >
        <GridLegacy
          container
          item
          columnSpacing={1.5}
          alignItems="center"
          xs={12}
        >
          {toolbarReset &&
            shouldSaveDatagridState &&
            (toolbarColumns || toolbarFilters) && (
              <GridLegacy item xs="auto">
                <DatagridConfigurationControls
                  apiRef={apiRef}
                  gridName={gridName}
                  handleResetState={handleResetState}
                />
              </GridLegacy>
            )}
          {(memoizedVisibleGroups || []).map((g) => (
            <GridLegacy item xs="auto" key={g.name}>
              <FullOrIconButton
                startIcon={g.icon}
                onClick={() => handleSetColumnGroup(g.name)}
                tooltip={g.label}
                color="primary"
                label={g.label}
                variant={undefined}
              />
            </GridLegacy>
          ))}
          {toolbarReset && (toolbarColumns || toolbarFilters) && (
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              sx={{ ml: 1.5 }}
            />
          )}
          {toolbarColumns && (
            <GridLegacy item xs="auto">
              <GridToolbarColumnsButton
                sx={narrowWindow ? narrowWindowToolbarButtonSx : undefined}
                placeholder=""
                onPointerEnterCapture={() => {}}
                onPointerLeaveCapture={() => {}}
              />
            </GridLegacy>
          )}
          {toolbarFilters && (
            <GridLegacy item xs="auto">
              <GridToolbarFilterButton
                sx={narrowWindow ? narrowWindowToolbarButtonSx : undefined}
                placeholder=""
                onPointerEnterCapture={() => {}}
                onPointerLeaveCapture={() => {}}
              />
            </GridLegacy>
          )}
          {toolbarExport && (
            <GridLegacy item xs="auto">
              {exportCsv ? (
                <FullOrIconButton
                  onClick={exportCsv}
                  tooltip="Export"
                  color="primary"
                  startIcon={<IconDownload />}
                  label="Export"
                  variant={undefined}
                  loadingButton
                  loading={isExportLoading}
                />
              ) : (
                <CustomExportButton<ReportDataType>
                  apiRef={apiRef}
                  columns={columns}
                  exportFields={exportFields}
                  exportFileNamePrefix={exportFileNamePrefix}
                  generateReportData={generateReportData}
                  gridName={gridName}
                  sx={narrowWindow ? narrowWindowToolbarButtonSx : undefined}
                />
              )}
            </GridLegacy>
          )}
          {toolbarQuickFilter && (
            <GridLegacy item xs="auto">
              <ExtendedQuickFilter
                searchText={searchText}
                setSearchText={setSearchText}
                handleChange={handleChange}
                quickSearchFocused={quickSearchFocused}
                setQuickSearchFocused={setQuickSearchFocused}
              />
            </GridLegacy>
          )}
          {(newRecordButton.onClick || uploadRecordButton.onClick) && (
            <GridLegacy
              item
              xs
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={actionButtonSpacing}
              >
                {uploadRecordButton.onClick && (
                  <FullOrIconButton
                    onClick={uploadRecordButton.onClick}
                    tooltip={uploadRecordButton.label}
                    color="primary"
                    disabled={uploadRecordButton.disabled}
                    permissionScope={uploadRecordButton.permissionScope}
                    startIcon={<IconUpload />}
                    variant="outlined"
                    label={uploadRecordButton.label}
                    trackingName={uploadRecordButton.trackingName}
                  />
                )}
                {newRecordButton.onClick && (
                  <FullOrIconButton
                    onClick={newRecordButton.onClick}
                    tooltip={newRecordButton.label}
                    color="primary"
                    disabled={newRecordButton.disabled}
                    permissionScope={newRecordButton.permissionScope}
                    startIcon={<IconCirclePlus />}
                    variant="contained"
                    label={newRecordButton.label}
                    trackingName={newRecordButton.trackingName}
                  />
                )}
              </Stack>
            </GridLegacy>
          )}
        </GridLegacy>
        <ActionBar
          rowActions={rowActions}
          selectedRowIds={selectedRowIds}
          selectedRowData={selectedRowData}
        />
        <GridLegacy
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          {children}
        </GridLegacy>
      </GridLegacy>
    </GridToolbarContainer>
  );
}
